import React from "react";
import { LucidePlus, Trash2 } from "lucide-react";
import TextInput from "../../../components/UI/TextInput";
import { Button } from "../../../components/UI/Button";
import Switch from "../../../components/UI/Switch";
import { contentUseCase } from "../../../../di/container";
import type { PortfolioLinkedOrder } from "../../../../domain/types/content";
import { PortfolioDescriptionEditor } from "./components/PortfolioDescriptionEditor";
import type { PortfolioButtonDraft } from "./components/PortfolioButtonsEditor";
import { PortfolioButtonsEditor } from "./components/PortfolioButtonsEditor";
import {
  isAllowedButtonUrl,
  PORTFOLIO_BUTTON_MAX_COUNT,
  PORTFOLIO_IMAGE_ACCEPT,
} from "./constants";

// 포트폴리오 추가 페이지(PortfolioCreatePage)와 목록의 수정 모달이 함께 쓰는 폼 조각.

/**
 * 편집 중인 버튼 목록을 서버가 받는 형태로 바꾼다.
 *
 * displayOrder 는 보내지 않는다. 백엔드가 배열 순서를 그대로
 * display_order 로 쓰고 이 필드는 무시한다.
 */
export const toButtonsRequest = (buttons: PortfolioButtonDraft[]) => ({
  buttons: buttons.map((button) => ({
    label: button.label.trim(),
    url: button.url.trim(),
    size: button.size,
  })),
});

/**
 * 저장 전에 버튼을 검사한다. 특히 javascript: 스킴을 여기서 막는다 —
 * 쇼핑몰이 이 값을 링크 href 로 출력하므로 통과시키면 방문자 브라우저에서 실행된다.
 * 백엔드도 같은 검증을 하지만 왕복 한 번 없이 알려주는 편이 낫다.
 */
export const findButtonProblem = (buttons: PortfolioButtonDraft[]): string | null => {
  /*
    편집기가 추가 버튼을 잠그므로 보통은 여기 걸리지 않는다. 다만 상한이
    생기기 전에 저장된 데이터를 불러오면 넘칠 수 있어 마지막으로 한 번 더 본다.
  */
  if (buttons.length > PORTFOLIO_BUTTON_MAX_COUNT) {
    return `버튼은 최대 ${PORTFOLIO_BUTTON_MAX_COUNT}개까지 넣을 수 있습니다. ${buttons.length - PORTFOLIO_BUTTON_MAX_COUNT}개를 지워주세요.`;
  }

  // tsconfig 의 target 이 es5 라 Array.prototype.entries() 는 못 돈다
  for (let index = 0; index < buttons.length; index += 1) {
    const button = buttons[index];
    const position = `${index + 1}번째 버튼`;
    if (button.label.trim() === "") return `${position}의 문구를 입력해주세요.`;
    if (!isAllowedButtonUrl(button.url)) {
      return `${position}의 이동 주소가 올바르지 않습니다. http:// 또는 https:// 로 시작하거나, 쇼핑몰 안의 경로면 / 로 시작해야 합니다.`;
    }
  }
  return null;
};

/** 추가 페이지·수정 모달이 다루는 폼 상태 */
export type PortfolioForm = {
  title: string;
  summary: string;
  description: string;
  clientName: string;
  /** yyyy-MM-dd */
  workedAt: string;
  isPublished: boolean;
};

export const EMPTY_FORM: PortfolioForm = {
  title: "",
  summary: "",
  description: "",
  clientName: "",
  workedAt: "",
  isPublished: false,
};

/**
 * 버튼과 연결 주문을 저장하는 단계들.
 *
 * 이 둘은 포트폴리오 본문과 다른 엔드포인트라, 한 번의 제출이 여러 단계로
 * 나뉜다. 백엔드에 이들을 묶는 트랜잭션이 없어 중간에 실패하면 부분 저장이
 * 남는다. 롤백 대신 어디까지 저장됐는지 알린다(runSaveSteps 주석 참조).
 *
 * 둘 다 "전체 치환"이라 비어 있어도 보낸다 — 마지막 버튼을 지운 경우에
 * 요청을 생략하면 서버에는 지우기 전 목록이 그대로 남는다.
 */
export const buildContentSaveSteps = (
  companyId: number,
  portfolioId: number,
  buttons: PortfolioButtonDraft[],
  orders: PortfolioLinkedOrder[]
) => [
  {
    label: "버튼",
    run: async () => {
      await contentUseCase.updatePortfolioButtons(
        companyId,
        portfolioId,
        toButtonsRequest(buttons)
      );
    },
  },
  {
    label: "주문 연결",
    run: async () => {
      await contentUseCase.updatePortfolioOrderLinks(companyId, portfolioId, {
        // 배열 순서가 곧 display_order 다
        orderIds: orders.map((order) => order.orderId),
      });
    },
  },
];


/** 추가 페이지와 수정 모달의 입력 영역은 동일하므로 한 곳에서 그린다 */
/*
  입력 항목이 9개가 되면서 위치 인자로는 어느 값이 어디로 가는지 알 수 없어
  객체로 받는다.
*/
export type PortfolioFormFieldsProps = {
  form: PortfolioForm;
  setForm: React.Dispatch<React.SetStateAction<PortfolioForm>>;
  thumbnailPreview: string | null;
  onThumbnailChange: (file: File) => void;
  selectedImageCount: number;
  onImagesChange: (files: File[]) => void;
  imagesHint: string;
  buttons: PortfolioButtonDraft[];
  onButtonsChange: (buttons: PortfolioButtonDraft[]) => void;
  orders: PortfolioLinkedOrder[];
  onOrdersChange: (orders: PortfolioLinkedOrder[]) => void;
  onOpenOrderPicker: () => void;
  /** 버튼·연결 주문을 못 읽었을 때의 사유. 있으면 두 항목을 편집하지 않는다 */
  contentError?: string | null;
};

export const PortfolioFormFields: React.FC<PortfolioFormFieldsProps> = ({
  form,
  setForm,
  thumbnailPreview,
  onThumbnailChange,
  selectedImageCount,
  onImagesChange,
  imagesHint,
  buttons,
  onButtonsChange,
  orders,
  onOrdersChange,
  onOpenOrderPicker,
  contentError = null,
}) => (
  <>
    <div className="text-start mb-[1.0625rem]">
      <label className="block text-sm font-medium mb-2">
        제목 <span className="text-[#F24A4A]">*</span>
      </label>
      <TextInput
        value={form.title}
        onChange={(e) => setForm({ ...form, title: e.target.value })}
        placeholder="진행한 프로젝트명"
        className="bg-gray-50"
      />
    </div>

    <div className="text-start mb-[1.0625rem]">
      <label className="block text-sm font-medium mb-2">한 줄 요약</label>
      <TextInput
        value={form.summary}
        onChange={(e) => setForm({ ...form, summary: e.target.value })}
        placeholder="목록 카드에 노출되는 한 줄 소개"
        className="bg-gray-50"
      />
    </div>

    <div className="text-start mb-[1.0625rem]">
      <label className="block text-sm font-medium mb-2">발주처</label>
      <TextInput
        value={form.clientName}
        onChange={(e) => setForm({ ...form, clientName: e.target.value })}
        placeholder="고객사 / 발주처"
        className="bg-gray-50"
      />
    </div>

    <div className="text-start mb-[1.0625rem]">
      <label className="block text-sm font-medium mb-2">진행 시기</label>
      <input
        type="date"
        value={form.workedAt}
        onChange={(e) => setForm({ ...form, workedAt: e.target.value })}
        className="w-full px-4 py-3 border border-[#e0e0e0] rounded-[8px] text-sm focus:border-[#4285F4] focus:outline-none bg-gray-50"
      />
    </div>

    <div className="text-start mb-[1.0625rem]">
      <label className="block text-sm font-medium mb-2">상세 설명</label>
      <PortfolioDescriptionEditor
        value={form.description}
        onChange={(description) => setForm({ ...form, description })}
      />
      <p className="mt-2 text-[12px] text-[#888]">
        마크다운으로 작성합니다. 오른쪽 미리보기가 쇼핑몰에 보일 모습입니다.
        마크다운을 쓰기 전에 저장한 글에 *, _, # 같은 문자가 들어 있으면
        의도하지 않은 서식으로 보일 수 있으니 미리보기에서 확인해주세요.
      </p>
    </div>

    <div className="text-start mb-[1.0625rem]">
      <label className="block text-sm font-medium mb-2">대표 이미지</label>

      <div className="w-[12.5rem]">
        <label>
          <div
            className="border
          rounded-[0.5rem]
          overflow-hidden
          w-[12.5rem]
          h-[12.5rem]
          bg-gray-50
          hover:bg-[#e6e6e6]
          transition-all
          relative
        "
          >
            {thumbnailPreview && (
              <img
                src={thumbnailPreview}
                alt="대표 이미지 미리보기"
                className="object-contain w-full h-full"
              />
            )}
          </div>

          <input
            type="file"
            className="hidden"
            accept={PORTFOLIO_IMAGE_ACCEPT}
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (!file) return;
              onThumbnailChange(file);
            }}
          />
        </label>
      </div>
    </div>

    <div className="text-start mb-[1.0625rem]">
      <label className="block text-sm font-medium mb-2">추가 이미지</label>
      <input
        type="file"
        multiple
        accept={PORTFOLIO_IMAGE_ACCEPT}
        className="w-full text-[13px] text-[#555]"
        onChange={(e) => {
          const files = Array.from(e.target.files || []);
          if (files.length === 0) return;
          onImagesChange(files);
        }}
      />
      <p className="mt-2 text-[12px] text-[#888]">
        {selectedImageCount > 0
          ? `${selectedImageCount}장 선택됨 — 저장 시 기존 이미지를 대체합니다.`
          : imagesHint}
      </p>
      <p className="mt-1 text-[12px] text-[#888]">
        PNG · JPG · GIF 를 올릴 수 있습니다. 움직이는 GIF 는 움직임을 유지한
        채로 저장됩니다.
      </p>
    </div>

    {contentError ? (
      <div
        className="text-start mb-[1.0625rem] / p-[0.875rem] / rounded-[8px]
          / bg-[#FDECEC] text-[#C0392B] / text-[13px]"
      >
        {contentError} 이번 수정에서는 버튼과 연결 주문을 건드리지 않습니다.
        나머지 항목은 그대로 저장됩니다.
      </div>
    ) : null}

    {contentError ? null : (
      <PortfolioButtonsEditor buttons={buttons} onChange={onButtonsChange} />
    )}

    <div
      className={`text-start mb-[1.0625rem] ${contentError ? "hidden" : ""}`}
    >
      <div className="flex flex-row items-center justify-between mb-2">
        <label className="block text-sm font-medium">연결된 주문</label>
        <Button variant="outline" onClick={onOpenOrderPicker}>
          <LucidePlus className="w-4 h-4" />
          주문 선택
        </Button>
      </div>

      {orders.length === 0 ? (
        <p className="text-[12px] text-[#888]">
          연결된 주문이 없습니다. 이 작업물로 만든 주문을 연결하면 어떤 상품과
          옵션으로 진행했는지 보여줄 수 있습니다.
        </p>
      ) : (
        <div className="flex flex-col gap-[0.5rem]">
          {orders.map((order) => (
            <div
              key={order.orderId}
              className="p-[0.75rem] / border border-[#e0e0e0] / rounded-[8px] / bg-gray-50
                / flex flex-row items-start justify-between gap-[0.75rem]"
            >
              <div className="min-w-0">
                <div className="text-sm">{order.productName}</div>
                <div className="text-[12px] text-[#888] mt-[0.125rem]">
                  {order.options.length > 0
                    ? order.options.join(" · ")
                    : "옵션 없음"}
                </div>
              </div>
              <button
                type="button"
                className="p-0.5 hover:bg-gray-200 rounded shrink-0"
                onClick={() =>
                  onOrdersChange(
                    orders.filter((item) => item.orderId !== order.orderId)
                  )
                }
                aria-label="연결 해제"
              >
                <Trash2 size={16} />
              </button>
            </div>
          ))}
        </div>
      )}

      <p className="mt-2 text-[12px] text-[#888]">
        쇼핑몰에는 상품명과 옵션 구성만 표시됩니다. 수량·금액과 주문자 정보는
        노출되지 않습니다.
      </p>
    </div>

    <div className="text-start mb-[1.0625rem] flex flex-row items-center justify-between">
      <label className="block text-sm font-medium">쇼핑몰 노출</label>
      <div
        onClick={() => setForm({ ...form, isPublished: !form.isPublished })}
        className="cursor-pointer"
      >
        <Switch isEnabled={form.isPublished} />
      </div>
    </div>
  </>
);
